// A bottom sheet for phones: slides up over a dimmed backdrop; tap outside, press Esc, or drag it down to close.
// The drag follows the finger (the backdrop fading with it), resists being pulled up, and a quick flick closes it
// even when it hasn't travelled far, like the sheets in Discord's app.
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { dimBar } from "../theme.ts";

/** Pulled down this far (px), or flicked faster than FLICK (px/ms), closes the sheet. */
const CLOSE_PX = 90;
const FLICK = 0.5;

export function Sheet({ onClose, label, children }: { onClose: () => void; label: string; children: React.ReactNode }) {
  const panel = useRef<HTMLDivElement>(null);
  const backdrop = useRef<HTMLDivElement>(null);
  const [closing, setClosing] = useState(false);
  const drag = useRef<{ y: number; dy: number; t: number; v: number; moved: boolean } | null>(null);
  // Ignore taps on the backdrop in the first moments: the finger that opened the sheet may still be lifting.
  const openedAt = useRef(performance.now());

  const close = () => {
    if (closing) return;
    setClosing(true);
    setTimeout(onClose, 180);
  };

  // The status bar dims with the app while the sheet is up.
  useEffect(() => dimBar(), []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });

  const reset = () => {
    const p = panel.current;
    const b = backdrop.current;
    if (p) {
      p.style.transition = "";
      p.style.transform = "";
    }
    if (b) {
      b.style.transition = "";
      b.style.opacity = "";
    }
  };

  return createPortal(
    <div
      ref={backdrop}
      className={`sheet-backdrop${closing ? " closing" : ""}`}
      onClick={(e) => e.target === e.currentTarget && performance.now() - openedAt.current > 350 && close()}
    >
      <div
        ref={panel}
        className={`sheet${closing ? " closing" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        onTouchStart={(e) => {
          // Drag down from anywhere while the sheet's content is scrolled to the top.
          const scroller = (e.target as Element).closest(".sheet-scroll, .emoji-grid-wrap, .gif-grid-wrap, .autocomplete-scroll");
          if (scroller && scroller.scrollTop > 0) return;
          drag.current = { y: e.touches[0]!.clientY, dy: 0, t: performance.now(), v: 0, moved: false };
        }}
        onTouchMove={(e) => {
          const d = drag.current;
          const p = panel.current;
          if (!d || !p) return;
          const raw = e.touches[0]!.clientY - d.y;
          // Scrolling the content up (finger moving up) is the content's, not ours.
          if (!d.moved && raw < 0) {
            drag.current = null;
            return;
          }
          if (!d.moved && raw < 6) return;
          d.moved = true;
          const now = performance.now();
          // Upward pulls give a little and stop (rubber band); downward ones follow exactly.
          const dy = raw >= 0 ? raw : -Math.sqrt(-raw) * 2;
          d.v = (dy - d.dy) / Math.max(1, now - d.t);
          d.dy = dy;
          d.t = now;
          p.style.transition = "none";
          p.style.transform = `translateY(${dy}px)`;
          if (backdrop.current) backdrop.current.style.transition = "none";
          if (backdrop.current) backdrop.current.style.opacity = String(Math.max(0.15, 1 - Math.max(0, dy) / (p.offsetHeight || 400)));
        }}
        onTouchEnd={() => {
          const d = drag.current;
          drag.current = null;
          if (!d || !d.moved || !panel.current) return;
          if (d.dy > CLOSE_PX || (d.v > FLICK && d.dy > 12)) {
            // Carry on down from where the finger let go.
            const p = panel.current;
            p.style.transition = "transform .18s cubic-bezier(.32,.72,0,1)";
            p.style.transform = "translateY(100%)";
            if (backdrop.current) {
              backdrop.current.style.transition = "opacity .18s";
              backdrop.current.style.opacity = "0";
            }
            setTimeout(onClose, 180);
            setClosing(true);
            return;
          }
          reset();
        }}
        onTouchCancel={() => {
          drag.current = null;
          reset();
        }}
      >
        <div className="sheet-grabber" aria-hidden="true" />
        {children}
      </div>
    </div>,
    document.body,
  );
}
